import { isAxiosError } from "axios";

export type ScheduleSubmitKind = "schedule" | "draft_save";

type ScheduleSubmitFailure =
  | "maybe_scheduled"
  | "maybe_saved"
  | "in_flight"
  | "already_submitted"
  | "partially_submitted";

const SCHEDULE_SUBMIT_FAILURE = Symbol("scheduleSubmitFailure");
const SCHEDULE_SUBMIT_REASON = Symbol("scheduleSubmitReason");

const SCHEDULE_SUBMIT_FAILURE_MESSAGES: Record<ScheduleSubmitFailure, string> =
  {
    maybe_scheduled:
      "Your post may already be scheduled. Check Upcoming before trying again.",
    maybe_saved:
      "Your draft may already be saved. Check Drafts before trying again.",
    in_flight:
      "This post is still being scheduled. Check Upcoming in a minute.",
    already_submitted: "This post was already scheduled. Check Upcoming.",
    partially_submitted:
      "Some of these posts were already scheduled. Check Upcoming before trying again.",
  };

const SCHEDULE_CONFLICT_FAILURES = new Map<string, ScheduleSubmitFailure>([
  ["SCHEDULE_IN_FLIGHT", "in_flight"],
  ["SCHEDULE_ALREADY_SUBMITTED", "already_submitted"],
  ["SCHEDULE_PARTIALLY_SUBMITTED", "partially_submitted"],
]);

function readScheduleSubmitFailure(
  error: unknown
): ScheduleSubmitFailure | undefined {
  if (!error || typeof error !== "object") return undefined;
  return (error as { [SCHEDULE_SUBMIT_FAILURE]?: ScheduleSubmitFailure })[
    SCHEDULE_SUBMIT_FAILURE
  ];
}

function readScheduleSubmitReason(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  return (error as { [SCHEDULE_SUBMIT_REASON]?: string })[
    SCHEDULE_SUBMIT_REASON
  ];
}

/** The server explains a partial submit, e.g. which post it refused. */
function readPartialSubmitReason(error: unknown): string | undefined {
  if (!isAxiosError(error)) return readScheduleSubmitReason(error);
  const data = error.response?.data as { error?: unknown } | undefined;
  return typeof data?.error === "string" && data.error ? data.error : undefined;
}

function classifyScheduleSubmitFailure(
  error: unknown,
  kind: ScheduleSubmitKind
): ScheduleSubmitFailure | undefined {
  if (!isAxiosError(error)) return undefined;
  const { response } = error;
  const timedOut = kind === "draft_save" ? "maybe_saved" : "maybe_scheduled";
  if (!response) {
    return error.code === "ERR_CANCELED" ? undefined : timedOut;
  }
  if (response.status === 504) return timedOut;
  const data = response.data as { code?: unknown } | undefined;
  if (response.status !== 409 || typeof data?.code !== "string") {
    return undefined;
  }
  return SCHEDULE_CONFLICT_FAILURES.get(data.code);
}

/**
 * Tags `target` when the schedule or draft save request behind `source`
 * failed in a way where the posts may still have been saved: a gateway
 * timeout, no response at all, or the server reporting the same submit is
 * still running, already scheduled, or partly scheduled. Only these submits
 * are tagged, so other failures keep their usual message.
 */
export function markScheduleSubmitFailure<T>(
  target: T,
  source: unknown = target,
  kind: ScheduleSubmitKind = "schedule"
): T {
  const failure =
    classifyScheduleSubmitFailure(source, kind) ??
    readScheduleSubmitFailure(source);
  if (failure && target && typeof target === "object") {
    (target as { [SCHEDULE_SUBMIT_FAILURE]?: ScheduleSubmitFailure })[
      SCHEDULE_SUBMIT_FAILURE
    ] = failure;
    const reason =
      failure === "partially_submitted"
        ? readPartialSubmitReason(source)
        : undefined;
    if (reason) {
      (target as { [SCHEDULE_SUBMIT_REASON]?: string })[
        SCHEDULE_SUBMIT_REASON
      ] = reason;
    }
  }
  return target;
}

export function getScheduleSubmitErrorMessage(
  error: unknown
): string | undefined {
  const failure = readScheduleSubmitFailure(error);
  if (!failure) return undefined;
  return (
    readScheduleSubmitReason(error) ?? SCHEDULE_SUBMIT_FAILURE_MESSAGES[failure]
  );
}

function isRejectedByValidation(error: unknown): boolean {
  if (!isAxiosError(error) || !error.response) return false;
  const { status } = error.response;
  return status >= 400 && status < 500 && status !== 409;
}

/**
 * Picks the error to show when several submits of one batch failed: a
 * validation error first, since retrying cannot fix it, then one that may
 * have been saved anyway, then the first.
 */
export function pickScheduleSubmitError(errors: unknown[]): unknown {
  return (
    errors.find((error) => isRejectedByValidation(error)) ??
    errors.find((error) => readScheduleSubmitFailure(error) !== undefined) ??
    errors[0]
  );
}
